import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import {
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subYears,
} from 'date-fns';
import DashboardPanelHeader from '../components/DashboardPanelHeader';
import useAuthorize from '../hooks/useAuthorize';
import { DASHBOARD_MODULE_ROLES } from '../roles/portalRoles';
import AppDownloadPieChart from './AppDownloadPieChart';
import AppDownloadTrendChart from './AppDownloadTrendChart';
import PlayMetricsChart from './PlayMetricsChart';
import {
  AppDownloadPeriod,
  AppDownloadResponse,
  fetchAppDownloads,
} from './appDownloadApi';

const LOOKBACK_YEARS = 2;
const TABLE_PAGE_SIZE = 30;

const formatRate = (value?: number | null) =>
  value == null ? '—' : `${(value * 100).toFixed(2)}%`;

const formatRating = (value?: number | null) =>
  value == null ? '—' : value.toFixed(2);

const DashboardApps = () => {
  useAuthorize(DASHBOARD_MODULE_ROLES);

  const getDefaultDateRange = () => {
    const now = new Date();
    return {
      fromDate: format(subYears(now, LOOKBACK_YEARS), 'yyyy-MM-dd'),
      toDate: format(now, 'yyyy-MM-dd'),
    };
  };

  const defaultDates = getDefaultDateRange();
  const [period, setPeriod] = useState<AppDownloadPeriod>('monthly');
  const [fromDate, setFromDate] = useState(defaultDates.fromDate);
  const [toDate, setToDate] = useState(defaultDates.toDate);
  const [quickRange, setQuickRange] = useState('last2Years');
  const [data, setData] = useState<AppDownloadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tablePage, setTablePage] = useState(0);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await fetchAppDownloads(
          fromDate,
          toDate,
          period,
          tablePage * TABLE_PAGE_SIZE,
          TABLE_PAGE_SIZE
        )
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load app downloads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setTablePage(0);
  }, [fromDate, toDate, period]);

  useEffect(() => {
    loadData();
  }, [fromDate, toDate, period, tablePage]);

  const handleQuickRangeChange = (event: any) => {
    const range = event.target.value;
    setQuickRange(range);
    if (!range) return;
    const now = new Date();
    let from = now;
    let to = now;
    switch (range) {
      case 'today':
        from = to = now;
        break;
      case 'yesterday':
        from = to = subDays(now, 1);
        break;
      case 'last7':
        from = subDays(now, 6);
        to = now;
        break;
      case 'thisWeek':
        from = startOfWeek(now, { weekStartsOn: 1 });
        to = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'thisMonth':
        from = startOfMonth(now);
        to = endOfMonth(now);
        break;
      case 'last2Years':
        from = subYears(now, LOOKBACK_YEARS);
        to = now;
        break;
      case 'ytd':
        from = startOfYear(now);
        to = now;
        break;
      default:
        break;
    }
    setFromDate(format(from, 'yyyy-MM-dd'));
    setToDate(format(to, 'yyyy-MM-dd'));
  };

  const totals = data?.totals || {
    android_downloads: 0,
    ios_downloads: 0,
    total_downloads: 0,
    daily_crashes: 0,
    daily_anrs: 0,
    daily_avg_rating: null,
    store_listing_visitors: 0,
    store_listing_acquisitions: 0,
    store_listing_conversion_rate: null,
    crash_rate: null,
    anr_rate: null,
  };

  const tableRows = data?.table?.rows || [];
  const tableTotal = data?.table?.total || 0;

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ mb: 2 }}>
        <DashboardPanelHeader
          title="Apps"
          subheader="Android and iOS downloads plus Google Play quality, ratings, and store-listing metrics."
          exportFilename="app-downloads"
          summaryRows={(data?.trend || []).map((row) => ({
            period: row.period,
            android_downloads: row.android_downloads,
            ios_downloads: row.ios_downloads,
            total_downloads: row.total_downloads,
            daily_crashes: row.daily_crashes,
            daily_anrs: row.daily_anrs,
            daily_avg_rating: row.daily_avg_rating,
            store_listing_visitors: row.store_listing_visitors,
            store_listing_acquisitions: row.store_listing_acquisitions,
            store_listing_conversion_rate: row.store_listing_conversion_rate,
          }))}
          summaryColumns={[
            { key: 'period', label: 'Period' },
            { key: 'android_downloads', label: 'Android' },
            { key: 'ios_downloads', label: 'iOS' },
            { key: 'total_downloads', label: 'Total' },
            { key: 'daily_crashes', label: 'Crashes' },
            { key: 'daily_anrs', label: 'ANRs' },
            { key: 'daily_avg_rating', label: 'Avg rating' },
            { key: 'store_listing_visitors', label: 'Store visitors' },
            { key: 'store_listing_acquisitions', label: 'Store acquisitions' },
            { key: 'store_listing_conversion_rate', label: 'Conversion rate' },
          ]}
        />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(event) => {
                  setFromDate(event.target.value);
                  setQuickRange('');
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={(event) => {
                  setToDate(event.target.value);
                  setQuickRange('');
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Chart period</InputLabel>
                <Select
                  value={period}
                  label="Chart period"
                  onChange={(event) => setPeriod(event.target.value as AppDownloadPeriod)}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Quick Range</InputLabel>
                <Select value={quickRange} label="Quick Range" onChange={handleQuickRangeChange}>
                  <MenuItem value="">Custom</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="last7">Last 7 Days</MenuItem>
                  <MenuItem value="thisWeek">This Week</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="last2Years">Last 2 Years</MenuItem>
                  <MenuItem value="ytd">Year to date</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {data?.sync?.message && (
        <Alert severity={data.sync.status === 'error' ? 'warning' : 'info'} sx={{ mb: 2 }}>
          Last sync: {data.sync.android_status || 'n/a'} Android, {data.sync.ios_status || 'n/a'} iOS.
          {data.sync.message ? ` ${data.sync.message}` : ''}
        </Alert>
      )}

      {loading ? (
        <Skeleton variant="rectangular" height={240} />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              ['Total downloads', totals.total_downloads, 'number'],
              ['Android', totals.android_downloads, 'number'],
              ['iOS', totals.ios_downloads, 'number'],
              ['Crashes', totals.daily_crashes, 'number'],
              ['ANRs', totals.daily_anrs, 'number'],
              ['Crash rate', totals.crash_rate, 'rate'],
              ['ANR rate', totals.anr_rate, 'rate'],
              ['Avg rating', totals.daily_avg_rating, 'rating'],
              ['Store visitors', totals.store_listing_visitors, 'number'],
              ['Store acquisitions', totals.store_listing_acquisitions, 'number'],
              ['Store conversion', totals.store_listing_conversion_rate, 'rate'],
            ].map(([label, value, kind]) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={String(label)}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="h5">
                      {kind === 'rate'
                        ? formatRate(value as number | null)
                        : kind === 'rating'
                          ? formatRating(value as number | null)
                          : Number(value || 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIphoneIcon fontSize="small" /> Downloads over time
                  </Typography>
                  <AppDownloadTrendChart data={data?.trend || []} period={period} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Android vs iOS
                  </Typography>
                  <AppDownloadPieChart
                    android={totals.android_downloads}
                    ios={totals.ios_downloads}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <PlayMetricsChart
                    title="Crashes, ANRs, and rates"
                    chartKey="quality"
                    data={data?.trend || []}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <PlayMetricsChart
                    title="Daily average rating"
                    chartKey="ratings"
                    data={data?.trend || []}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <PlayMetricsChart
                    title="Store listing visitors, acquisitions, and conversion"
                    chartKey="store"
                    data={data?.trend || []}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Daily detail
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Newest days first. Use the pager to scroll back through the selected range.
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Android</TableCell>
                        <TableCell align="right">iOS</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Crashes</TableCell>
                        <TableCell align="right">ANRs</TableCell>
                        <TableCell align="right">Crash rate</TableCell>
                        <TableCell align="right">Rating</TableCell>
                        <TableCell align="right">Visitors</TableCell>
                        <TableCell align="right">Acquisitions</TableCell>
                        <TableCell align="right">Conversion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11}>
                            No store snapshots yet for this date range. Data is refreshed nightly
                            from Google Play and App Store Connect.
                          </TableCell>
                        </TableRow>
                      ) : (
                        tableRows.map((row) => (
                          <TableRow key={row.period}>
                            <TableCell>{row.period}</TableCell>
                            <TableCell align="right">
                              {Number(row.android_downloads).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {Number(row.ios_downloads).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {Number(row.total_downloads).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {Number(row.daily_crashes || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {Number(row.daily_anrs || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">{formatRate(row.crash_rate)}</TableCell>
                            <TableCell align="right">{formatRating(row.daily_avg_rating)}</TableCell>
                            <TableCell align="right">
                              {Number(row.store_listing_visitors || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {Number(row.store_listing_acquisitions || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {formatRate(row.store_listing_conversion_rate)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={tableTotal}
                    page={tablePage}
                    onPageChange={(_, nextPage) => setTablePage(nextPage)}
                    rowsPerPage={TABLE_PAGE_SIZE}
                    rowsPerPageOptions={[TABLE_PAGE_SIZE]}
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} of ${count} days (newest first)`
                    }
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default DashboardApps;
